import type { ReformatResult } from "@core/FileStructue/Catalog/reformatCatalog";
import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import GitIndexService from "@core-ui/ContextServices/GitIndexService";
import ModalToOpenService from "@core-ui/ContextServices/ModalToOpenService/ModalToOpenService";
import ModalToOpen from "@core-ui/ContextServices/ModalToOpenService/model/ModalsToOpen";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import reformatPending from "@ext/catalog/actions/reformat/reformatPending";
import { usePublishPanel } from "@ext/git/core/GitPublish/PublishPanel/usePublishPanel";
import type { ComponentProps } from "react";
import { useCallback, useState } from "react";
import type ReformatCatalogConfirm from "./ReformatCatalogConfirm";
import type ReformatCatalogReport from "./ReformatCatalogReport";

const useReformatCatalog = () => {
	const catalogName = useCatalogPropsStore((state) => state.data?.name);
	const apiUrlCreator = ApiUrlCreatorService.value;
	const changed = GitIndexService.getStatus().size;
	const { open: openPublishPanel } = usePublishPanel();
	const [isRunning, setIsRunning] = useState(false);

	const goToPublish = useCallback(() => {
		ModalToOpenService.resetValue();
		openPublishPanel();
	}, [openPublishPanel]);

	const showReport = useCallback(
		(result: ReformatResult) => {
			ModalToOpenService.setValue<ComponentProps<typeof ReformatCatalogReport>>(
				ModalToOpen.ReformatCatalogReport,
				{
					result,
					onClose: () => ModalToOpenService.resetValue(),
					onGoToPublish: goToPublish,
				},
			);
		},
		[goToPublish],
	);

	// The confirmation stays open while the run goes: the report replaces it, a failure closes it.
	const reformat = useCallback(async () => {
		setIsRunning(true);

		const response = await FetchService.fetch<ReformatResult>(apiUrlCreator.reformatCatalog());
		setIsRunning(false);
		if (!response?.ok) {
			ModalToOpenService.resetValue();
			return;
		}

		const result = await response.json();

		if (result.written > 0) reformatPending.mark(catalogName);
		showReport(result);
		// apiUrlCreator is not in the list: it changes with the catalog, and catalogName already is.
	}, [catalogName, showReport]);

	const askToReformat = useCallback(() => {
		ModalToOpenService.setValue<ComponentProps<typeof ReformatCatalogConfirm>>(ModalToOpen.ReformatCatalogConfirm, {
			hasUnpublished: changed > 0,
			onCancel: () => ModalToOpenService.resetValue(),
			onConfirm: reformat,
			onGoToPublish: goToPublish,
		});
	}, [changed, goToPublish, reformat]);

	return { isRunning, askToReformat };
};

export default useReformatCatalog;
